// Pure builders + converters for the Daily Rota.
//
// A day's blocks are of two kinds:
//   • ROTA-OWNED  (meals, night duty, custom) — the rota manages these fully:
//     move, resize, title, staff, add/remove. Saved verbatim.
//   • REFLECTED   (Activities aft/eve, Classes morning) — owned by their own
//     modules and pulled in read-only. Their STAFF is never editable here; only
//     their time can be nudged (arranging the day is the rota's job). We store
//     just a time OVERRIDE keyed by refKey and re-derive staff live each load,
//     so there's a single source of truth for who's on an activity / class.
//
// Editor blocks use minutes-from-midnight for start/end; the stored contract
// uses "HH:mm".

export function timeToMin(t) {
  if (typeof t === "number") return t;
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(t || "").trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : 0;
}

export function minToTime(min) {
  const v = Math.max(0, Math.round(min));
  const h = Math.floor(v / 60);
  const mm = v % 60;
  return `${h < 10 ? "0" : ""}${h}:${mm < 10 ? "0" : ""}${mm}`;
}

const isReflectedSource = (src) => src === "activity" || src === "class";

// ---- hours ----------------------------------------------------------------
//
// The BASE calculation of assigned hours is the backend's, returned per person as
// `scheduledMinutes` on the `api/activities/leaders` feed. That figure is a
// snapshot — it doesn't move as the manager edits — so the planner keeps a
// working figure locally:
//
//   working = baseline + (localMinutesNow − localMinutesAtLoad) − clawback
//
// We trust the backend number at load and apply only the DELTA our own edits
// make, which is immune to any mismatch between what the backend counted and
// what the front end can see. With no baseline (field absent, e.g. prod before
// the endpoint ships) the local figure stands on its own.
//
// The baseline only covers people on the activity-leaders feed, which is
// role-filtered — a teacher, or anyone rostered onto a block who doesn't lead
// activities, simply isn't in it. They still work hours, so for them the local
// figure stands on its own too, person by person: without that they'd read as a
// flat 0h (base 0, and delta 0 because the load-time figure cancels itself).
//
// The local calculation mirrors the backend's rules:
//   • meals and explicitly-unpaid blocks don't count;
//   • a CLASS block always credits 3 hours, however long its band runs;
//   • concurrent blocks aren't double counted — a person on task A 09:00–10:00
//     and task B 09:30–10:30 has worked 1h30, not 2h.
// The overtime claw-back for excused residential presence is front-end only:
// the backend reports WHO is excused (`excusedFromResidential`) but never nets
// it off `scheduledMinutes`.

// A block counts toward a person's working hours unless it's a meal or an
// explicitly-unpaid block (meals, evening residential presence).
export function countsHours(block) {
  return !!block && block.kind !== "meal" && block.paid !== "no";
}

export const HOURS_LIMIT_MIN = 7 * 60; // highlight anyone assigned over 7 hours
export const CLASS_CREDIT_MIN = 3 * 60; // a class block is 3 hours' work, always

export const nameKeyOf = (name) => String(name || "").trim().toLowerCase();

// ---- staff identity -------------------------------------------------------
//
// Hours key on staffId, so two people who share a name don't merge into one
// total. Two things stop that being a straight swap:
//
//   • the `staffId` on a block's person ISN'T reliably a staffId — an activity's
//     ADULT members carry their adultId (`personId`), and the residential block
//     and the rail fall back to `adultId` when the weekly feed has no staffId;
//   • not everyone has an id at all — a class arrives from the Classes feed as a
//     bare `leaderName`, and a manager can type a free-text name onto a block.
//
// So we resolve through an index built from the feeds that carry staffId, adultId
// and name together (activity-leaders + weekly staff scheduling), and fall back
// to a name key for whoever can't be pinned to an id. With no index at all
// (feeds unavailable) everything falls back to names — i.e. the old behaviour —
// rather than trusting ids we can't tell apart.

const STAFF_PREFIX = "#";
const NAME_PREFIX = "~";
const EMPTY_INDEX = { byStaffId: new Set(), adultToStaff: new Map(), nameToStaff: new Map() };

const weeklyRows = (weekly) =>
  asArray(weekly && weekly.rows).length ? weekly.rows : asArray(weekly);

export function buildStaffIndex({ leaders, weekly } = {}) {
  const byStaffId = new Set();
  const adultToStaff = new Map();
  const nameToStaff = new Map(); // nameKey -> staffId, or null when shared by two staff
  [...asArray(leaders), ...weeklyRows(weekly)].forEach((r) => {
    if (!r || r.staffId === null || r.staffId === undefined) return;
    const sid = String(r.staffId);
    byStaffId.add(sid);
    if (r.adultId !== null && r.adultId !== undefined) adultToStaff.set(String(r.adultId), sid);
    const nk = nameKeyOf(r.name);
    if (!nk) return;
    if (!nameToStaff.has(nk)) nameToStaff.set(nk, sid);
    else if (nameToStaff.get(nk) !== sid) nameToStaff.set(nk, null); // ambiguous — two staff, one name
  });
  return { byStaffId, adultToStaff, nameToStaff };
}

// Canonical hours key for a person (or a bare name string): `#<staffId>` when we
// can pin one, else `~<name>`.
export function staffKeyOf(person, index) {
  const p = typeof person === "string" ? { name: person } : person || {};
  const idx = index || EMPTY_INDEX;
  const nk = nameKeyOf(p.name);
  const ids = [p.staffId, p.adultId].filter((v) => v !== null && v !== undefined).map(String);
  // The id we hold may be either a staffId or an adultId — try it as both.
  const direct = ids.find((v) => idx.byStaffId.has(v));
  if (direct) return STAFF_PREFIX + direct;
  const viaAdult = ids.map((v) => idx.adultToStaff.get(v)).find(Boolean);
  if (viaAdult) return STAFF_PREFIX + viaAdult;
  const viaName = nk ? idx.nameToStaff.get(nk) : null;
  if (viaName) return STAFF_PREFIX + viaName;
  return nk ? NAME_PREFIX + nk : "";
}

// The interval a block actually CREDITS: its clock span, except for classes,
// which pay 3 hours from their start whatever the band's elapsed time. Returning
// an interval (not just a duration) keeps the overlap merge below uniform.
export function creditedInterval(block) {
  const start = block.start;
  const end = block.kind === "class" ? start + CLASS_CREDIT_MIN : block.end;
  return [start, Math.max(start, end)];
}

// Total length of a set of [start, end] intervals with overlaps merged, so
// concurrent work counts once.
function unionMinutes(intervals) {
  const sorted = intervals.filter(([s, e]) => e > s).sort((a, b) => a[0] - b[0]);
  let total = 0;
  let curStart = null;
  let curEnd = null;
  sorted.forEach(([s, e]) => {
    if (curEnd === null || s > curEnd) {
      if (curEnd !== null) total += curEnd - curStart;
      curStart = s;
      curEnd = e;
    } else if (e > curEnd) {
      curEnd = e;
    }
  });
  if (curEnd !== null) total += curEnd - curStart;
  return total;
}

// Counting minutes per person, keyed by staffKeyOf so it works across rota,
// activity, class and residential assignments. Overlaps merge; meals +
// residential presence don't add; classes credit 3h.
export function computeStaffMinutes(blocks, index) {
  const spans = {}; // staffKey -> [[start, end], ...]
  (blocks || []).forEach((b) => {
    if (!countsHours(b)) return;
    const iv = creditedInterval(b);
    (b.people || []).forEach((p) => {
      const key = staffKeyOf(p, index);
      if (!key) return;
      if (!spans[key]) spans[key] = [];
      spans[key].push(iv);
    });
  });
  const totals = {};
  Object.keys(spans).forEach((k) => { totals[k] = unionMinutes(spans[k]); });
  return totals;
}

// The backend's base calculation, off the activity-leaders feed. Returns null
// when NO row carries the field — that's "the backend hasn't shipped it here",
// which is different from "everybody is on zero minutes" and makes the view fall
// back to a purely local figure.
export function buildHoursBaseline(leaders, index) {
  const out = {};
  let sawField = false;
  asArray(leaders).forEach((l) => {
    const mins = Number(l && l.scheduledMinutes);
    if (l == null || l.scheduledMinutes == null || !Number.isFinite(mins)) return;
    sawField = true;
    const key = staffKeyOf(l, index);
    if (key) out[key] = Math.max(0, Math.round(mins));
  });
  return sawField ? out : null;
}

// A residential block's `excused` list holds person keys (staffKeyOf). Saves made
// before hours moved onto staffId hold plain lowercased names, so accept both.
export const excusedKeyOf = (entry, index) =>
  typeof entry === "string" && (entry.startsWith(STAFF_PREFIX) || entry.startsWith(NAME_PREFIX))
    ? entry
    : staffKeyOf(String(entry || ""), index);

// Who the backend already has flagged as excused from residential presence.
export function excusedFromFeed(leaders, index) {
  return asArray(leaders)
    .filter((l) => l && l.excusedFromResidential === true)
    .map((l) => staffKeyOf(l, index))
    .filter(Boolean);
}

// Minutes of residential presence each person has been EXCUSED from — the pool
// available to claw overtime back from.
function computeClawbackPool(blocks, index) {
  const pool = {};
  (blocks || []).forEach((b) => {
    if (!b.residential || !Array.isArray(b.excused)) return;
    const dur = Math.max(0, b.end - b.start);
    b.excused.forEach((entry) => {
      const key = excusedKeyOf(entry, index);
      if (key) pool[key] = (pool[key] || 0) + dur;
    });
  });
  return pool;
}

// Effective hours per person. `baseline` is the backend's base calculation and
// `baselineAt` the local figure for the same day AS LOADED; together they turn
// the local calculation into a delta on top of the backend number. Omit both for
// a purely local absolute figure — as does anyone the baseline doesn't cover.
//
// Excusing an over-hours person from residential presence credits up to that
// block's duration, but only enough to reach the 7h limit (never below): a 7h30
// person excused from a 1h residential block lands at 7h, not 6h30.
//
// Returns { [staffKey]: { baseline, delta, raw, clawback, effective, over } }.
export function computeStaffHours(blocks, { baseline, baselineAt, index } = {}) {
  const local = computeStaffMinutes(blocks, index);
  const clawAvail = computeClawbackPool(blocks, index);
  const hasBaseline = !!baseline;
  const at = baselineAt || {};

  const keys = new Set([...Object.keys(local), ...Object.keys(clawAvail)]);
  if (hasBaseline) Object.keys(baseline).forEach((k) => keys.add(k));

  const out = {};
  keys.forEach((k) => {
    // Uncovered people fall back to their own at-load figure as the base, so the
    // delta lands on top of the hours the rota can actually see rather than 0.
    const covered = hasBaseline && Object.prototype.hasOwnProperty.call(baseline, k);
    const base = covered ? baseline[k] : hasBaseline ? at[k] || 0 : 0;
    const delta = hasBaseline ? (local[k] || 0) - (at[k] || 0) : 0;
    const raw = Math.max(0, hasBaseline ? base + delta : local[k] || 0);
    const overage = Math.max(0, raw - HOURS_LIMIT_MIN);
    const clawback = Math.min(overage, clawAvail[k] || 0);
    const effective = raw - clawback;
    out[k] = { baseline: base, delta, raw, clawback, effective, over: effective > HOURS_LIMIT_MIN };
  });
  return out;
}

function asArray(v) {
  if (Array.isArray(v)) return v;
  if (v && Array.isArray(v.groups)) return v.groups;
  if (v && Array.isArray(v.rows)) return v.rows;
  return [];
}

// Rota-owned day skeleton (NO morning classes — that's reflected from Classes).
export const DAY_TEMPLATE = [
  { title: "Breakfast", kind: "meal", start: "07:30", end: "08:30" },
  { title: "Dinner", kind: "meal", start: "18:00", end: "19:00" },
  { title: "Night duty", kind: "night", start: "21:30", end: "23:00" },
];

// Weekends have no lunch block (packed lunch on the go).
export function isWeekend(dateStr) {
  const [y, m, d] = String(dateStr || "").split("-").map(Number);
  if (!y || !m || !d) return false;
  const day = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0=Sun, 6=Sat
  return day === 0 || day === 6;
}

// Default time bands (editable, not authoritative — Classes/Activities carry no
// clock time). Mirrors the Activity Rota PDF's 2–4pm / 7–9pm and a morning band.
const CLASS_BAND = ["09:00", "12:30"];
const SLOT_BAND = { DAY: ["14:00", "16:00"], AFTERNOON: ["14:00", "16:00"], EVENING: ["19:00", "21:00"] };

// Set of every known staff id (adultId + staffId) from the weekly staff feed and
// the activity leaders. Used to keep NON-staff adults (e.g. visiting group
// leaders) out of the reflected activity blocks.
export function collectStaffIds(weekly, leaders) {
  const ids = new Set();
  const add = (v) => {
    if (v !== null && v !== undefined) ids.add(String(v));
  };
  (asArray(weekly && weekly.rows).length ? weekly.rows : asArray(weekly)).forEach((r) => {
    if (r.staff) {
      add(r.adultId);
      add(r.staffId);
    }
  });
  asArray(leaders).forEach((l) => {
    add(l.adultId);
    add(l.staffId);
  });
  return ids;
}

// REFLECTED: the day's activity groups → one read-only block per slot, listing
// the STAFF on it (leader + adult members who are staff) with the group name as
// their role. Non-staff adults are excluded. `staffIds` gates the members; when
// it's absent/empty we can't tell, so we keep all adults (backward-compatible).
export function buildActivityBlocks(groups, leaders, staffIds) {
  const leaderById = new Map(asArray(leaders).map((l) => [String(l.staffId), l]));
  const staffOnly = staffIds && staffIds.size > 0;
  const buckets = { AFTERNOON: [], EVENING: [] };
  asArray(groups).forEach((g) => {
    const slotKey = String(g.slot || "").toUpperCase() === "EVENING" ? "EVENING" : "AFTERNOON";
    const role = g.name || "activity";
    const leaderName = (leaderById.get(String(g.leaderStaffId)) || {}).name || g.leaderName;
    if (leaderName) buckets[slotKey].push({ name: leaderName, role, staffId: g.leaderStaffId ?? null });
    (g.members || [])
      .filter((m) => String(m.personType || "").toUpperCase() === "ADULT")
      .filter((m) => !staffOnly || staffIds.has(String(m.personId)))
      .forEach((m) => buckets[slotKey].push({ name: m.name, role, staffId: m.personId ?? null }));
  });
  const blocks = [];
  if (buckets.AFTERNOON.length) {
    blocks.push({ title: "Afternoon activities", kind: "act", start: SLOT_BAND.AFTERNOON[0], end: SLOT_BAND.AFTERNOON[1], people: buckets.AFTERNOON, source: "activity", refKey: "act:afternoon" });
  }
  if (buckets.EVENING.length) {
    blocks.push({ title: "Evening activities", kind: "act", start: SLOT_BAND.EVENING[0], end: SLOT_BAND.EVENING[1], people: buckets.EVENING, source: "activity", refKey: "act:evening" });
  }
  return blocks;
}

// REFLECTED: the day's classes → one read-only "Morning classes" block, each
// class's teacher (leader) listed with the class name as their role.
export function buildClassBlocks(classes, dateStr) {
  const list = asArray(classes).filter(
    (c) => !dateStr || !Array.isArray(c.groupDates) || c.groupDates.includes(dateStr)
  );
  if (!list.length) return [];
  const people = list.map((c) => ({ name: c.leaderName || "(no teacher)", role: c.groupName || "class", staffId: null }));
  return [{ title: "Morning classes", kind: "class", start: CLASS_BAND[0], end: CLASS_BAND[1], people, source: "class", refKey: "class:morning" }];
}

// Evening residential presence — a rota-owned block that's ALWAYS present and
// auto-filled (best-effort) with the on-duty residential staff. It's editable
// (add / remove / excuse); unpaid so it doesn't count toward hours. There's no
// owning module for residential staff, so it isn't reflected. Detection is
// lenient because the weekly feed's residential flag name isn't guaranteed.
// `excused` are person keys (staffKeyOf) the backend already reports as excused —
// they start off the block, and in `excused` so their overtime is clawed back.
export function buildResidentialBlock(weekly, dateStr, { excused: excusedIn = [], index } = {}) {
  const rows = weeklyRows(weekly);
  const truthy = (v) => v === true || v === 1 || v === "true";
  const isResident = (r) =>
    r.staff && inStay(r, dateStr) && onDuty(r, dateStr) &&
    (truthy(r.overnightResident) || truthy(r.residential) || truthy(r.resident) || truthy(r.overnight));
  const excused = [...new Set(excusedIn.map((e) => excusedKeyOf(e, index)).filter(Boolean))];
  const people = rows
    .filter(isResident)
    .map((r) => ({ name: r.name, role: "", staffId: r.staffId ?? r.adultId ?? null }))
    .filter((p) => !excused.includes(staffKeyOf(p, index)));
  return {
    title: "Evening residential presence",
    kind: "custom",
    start: "21:00",
    end: "22:00",
    people,
    excused,
    source: "rota",
    paid: "no",
    residential: true,
    notes: "Pastoral availability within residential areas.",
  };
}

export function buildReflectedBlocks({ groups, leaders, classes, dateStr, staffIds } = {}) {
  return [...buildClassBlocks(classes, dateStr), ...buildActivityBlocks(groups, leaders, staffIds)];
}

// Normalise a mix of pre-editor blocks (HH:mm strings) into editor blocks
// (minutes, ids, reflected flag). Reflected blocks use their refKey as id so a
// saved time-override re-attaches to them; rota-owned get positional ids.
function withEditorShape(blocks) {
  return blocks
    .map((b) => {
      const source = b.source || "rota";
      return {
        title: b.title || "Untitled",
        kind: b.kind || "custom",
        start: timeToMin(b.start),
        end: timeToMin(b.end),
        people: (b.people || []).map((p) => ({ name: p.name, role: p.role || "", staffId: p.staffId ?? null })),
        source,
        refKey: b.refKey || null,
        reflected: isReflectedSource(source),
        paid: b.paid || null, // "yes" | "no" | "conditional" (paid if assigned) | null
        notes: b.notes || "",
        residential: !!b.residential, // the auto-filled evening residential block
        excused: Array.isArray(b.excused) ? b.excused : [], // residential: person keys excused (→ overtime claw-back)
      };
    })
    .sort((a, b) => a.start - b.start)
    .map((b, i) => ({ ...b, id: b.reflected && b.refKey ? b.refKey : `s${i}` }));
}

// Fresh day (no saved rota): rota template + reflected activities + classes.
export function buildSeedBlocks({ groups, leaders, classes, dateStr, staffIds, weekly, index } = {}) {
  const idx = index || buildStaffIndex({ leaders, weekly });
  const base = isWeekend(dateStr)
    ? DAY_TEMPLATE // no lunch on weekends
    : [...DAY_TEMPLATE, { title: "Lunch", kind: "meal", start: "12:30", end: "13:30" }];
  const rotaOwned = [
    ...base.map((b) => ({ ...b, people: [], source: "rota" })),
    buildResidentialBlock(weekly, dateStr, { excused: excusedFromFeed(leaders, idx), index: idx }),
  ];
  return withEditorShape([...rotaOwned, ...buildReflectedBlocks({ groups, leaders, classes, dateStr, staffIds })]);
}

// Saved rota exists: take the rota-owned blocks verbatim, and re-derive the
// reflected blocks live — applying any saved time-override by refKey.
export function mergeSaved(saved, reflectedInputs) {
  const { weekly, dateStr, leaders, index } = reflectedInputs || {};
  const idx = index || buildStaffIndex({ leaders, weekly });
  const reflectedPre = buildReflectedBlocks(reflectedInputs);
  const feedExcused = excusedFromFeed(leaders, idx);
  const overrides = new Map();
  const rotaOwned = [];
  asArray(saved && saved.blocks).forEach((b) => {
    if (isReflectedSource(b.source) && b.refKey) {
      overrides.set(b.refKey, { start: b.start, end: b.end });
    } else {
      rotaOwned.push({ ...b, source: "rota" });
    }
  });
  const reflected = reflectedPre.map((rb) => {
    const ov = overrides.get(rb.refKey);
    return ov ? { ...rb, start: ov.start, end: ov.end } : rb;
  });
  // Always ensure the evening residential block is present (older saves lack it).
  const hasResidential = rotaOwned.some((b) => b.residential || b.title === "Evening residential presence");
  if (!hasResidential) {
    rotaOwned.push(buildResidentialBlock(weekly, dateStr, { excused: feedExcused, index: idx }));
  }
  // Normalise the residential block's excused list to person keys (older saves
  // hold names) and fold the backend's excusals in, so the two sources can't
  // disagree: an excused person is off the block and in `excused`.
  return withEditorShape(
    [...rotaOwned, ...reflected].map((b) => {
      if (!b.residential) return b;
      const excused = [
        ...new Set([...(b.excused || []).map((e) => excusedKeyOf(e, idx)), ...feedExcused].filter(Boolean)),
      ];
      return { ...b, excused, people: (b.people || []).filter((p) => !excused.includes(staffKeyOf(p, idx))) };
    })
  );
}

// For persistence: rota-owned blocks in full; reflected blocks as time-only
// overrides (source + refKey + times) — never their staff.
export function toSaved(blocks) {
  return (blocks || []).map((b) =>
    b.reflected
      ? { source: b.source, refKey: b.refKey, start: minToTime(b.start), end: minToTime(b.end) }
      : {
          title: b.title,
          kind: b.kind,
          source: "rota",
          start: minToTime(b.start),
          end: minToTime(b.end),
          paid: b.paid || null,
          notes: b.notes || "",
          ...(b.residential ? { residential: true } : {}),
          ...(b.excused && b.excused.length ? { excused: b.excused } : {}),
          people: (b.people || []).map((p) => ({ name: p.name, role: p.role || "", staffId: p.staffId ?? null })),
        }
  );
}

// Side-by-side lane assignment for overlapping blocks (calendar-column style).
// Returns { [id]: { lane, lanes } } — the block's column index within its
// overlap cluster and the cluster's total column count. Non-overlapping blocks
// get { lane: 0, lanes: 1 } (full width). Used so crowded blocks sit next to
// each other instead of stacking on top.
export function assignBlockLanes(blocks) {
  const out = {};
  const sorted = [...(blocks || [])].sort((a, b) => a.start - b.start || a.end - b.end);
  let cluster = [];
  let clusterEnd = -Infinity;

  const flush = () => {
    const laneEnds = []; // laneEnds[i] = end time of the last block placed in lane i
    const laneOf = new Map();
    cluster.forEach((blk) => {
      let placed = -1;
      for (let i = 0; i < laneEnds.length; i += 1) {
        if (blk.start >= laneEnds[i]) { laneEnds[i] = blk.end; placed = i; break; }
      }
      if (placed === -1) { placed = laneEnds.length; laneEnds.push(blk.end); }
      laneOf.set(blk.id, placed);
    });
    const lanes = laneEnds.length;
    cluster.forEach((blk) => { out[blk.id] = { lane: laneOf.get(blk.id), lanes }; });
    cluster = [];
    clusterEnd = -Infinity;
  };

  sorted.forEach((blk) => {
    if (cluster.length && blk.start >= clusterEnd) flush();
    cluster.push(blk);
    clusterEnd = Math.max(clusterEnd, blk.end);
  });
  if (cluster.length) flush();
  return out;
}

// ---- rota templates (per-campus, structure-only, ROTA-OWNED blocks only) ---

export function toTemplateBlocks(blocks) {
  return (blocks || [])
    .filter((b) => !b.reflected && !b.residential) // residential auto-appears; don't bake it into a template
    .map((b) => ({ title: b.title, kind: b.kind, start: minToTime(b.start), end: minToTime(b.end), paid: b.paid || null, notes: b.notes || "" }));
}

// Apply a template: its blocks become rota-owned; the caller keeps the live
// reflected blocks (see DailyRotaView.applyTemplate).
export function blocksFromTemplate(tpl) {
  return withEditorShape(asArray(tpl && tpl.blocks).map((b) => ({ ...b, people: [], source: "rota" })));
}

export const DEFAULT_TEMPLATES = [
  {
    // Employee-handbook standard weekday. The 14:00–16:00 and 19:00–21:00
    // activity blocks are omitted — they reflect live from the Activities module
    // at exactly those bands. `paid`: "yes" | "no" | "conditional" (if assigned).
    id: "builtin-weekday",
    name: "Standard weekday",
    builtin: true,
    blocks: [
      { title: "Morning wake-up duty", kind: "custom", start: "07:00", end: "08:00", paid: "conditional", notes: "Assist students in preparing for the day." },
      { title: "Breakfast", kind: "meal", start: "08:00", end: "08:30" },
      { title: "Lunch", kind: "meal", start: "12:30", end: "13:00" },
      { title: "Assembly", kind: "custom", start: "13:30", end: "14:00", paid: "conditional", notes: "Attend and assist with daily announcements." },
      { title: "Free time monitoring", kind: "custom", start: "16:00", end: "18:00", paid: "yes", notes: "Provide structured supervision during free time." },
      { title: "Dinner", kind: "meal", start: "18:00", end: "18:30" },
      { title: "Lights-out supervision", kind: "night", start: "22:00", end: "23:00", paid: "conditional", notes: "Ensure quiet and bedtime compliance." },
    ],
  },
  {
    // Employee-handbook excursion weekend. The 19:00–21:00 evening activity is
    // omitted — it reflects live from the Activities module.
    id: "builtin-weekend",
    name: "Standard weekend",
    builtin: true,
    blocks: [
      { title: "Morning wake-up duty", kind: "custom", start: "07:00", end: "08:00", paid: "conditional", notes: "Assist students in preparing for the day." },
      { title: "Breakfast", kind: "meal", start: "08:00", end: "08:30" },
      { title: "Assembly", kind: "custom", start: "09:00", end: "09:30", paid: "conditional", notes: "Attend and assist with trip announcements." },
      { title: "Excursion supervision", kind: "custom", start: "09:30", end: "16:30", paid: "yes", notes: "Full engagement required, including headcounts and transitions." },
      { title: "Dinner", kind: "meal", start: "18:00", end: "18:30" },
      { title: "Lights-out supervision", kind: "night", start: "22:00", end: "23:00", paid: "conditional", notes: "Ensure quiet and bedtime compliance." },
    ],
  },
  {
    // Overlay add-on: airport transfer duties layered ON TOP of the day's base
    // (airport days are normally excursion days too). Applying it ADDS these
    // blocks rather than replacing.
    id: "builtin-airport",
    name: "Airport day",
    builtin: true,
    overlay: true,
    blocks: [
      { title: "Airport Team", kind: "custom", start: "09:00", end: "17:00", paid: "yes" },
      { title: "Campus Welcome Team", kind: "custom", start: "09:00", end: "17:00", paid: "yes" },
    ],
  },
  {
    // Loretto's standard daily schedule, offered to every campus as a starter.
    // Rota-owned skeleton only — Class Time, Afternoon and Evening Activities
    // are omitted because they reflect live from the Classes / Activities
    // modules.
    id: "builtin-loretto-standard",
    name: "Loretto standard day",
    builtin: true,
    blocks: [
      { title: "Wake up", kind: "custom", start: "07:00", end: "08:00" },
      { title: "Breakfast", kind: "meal", start: "08:00", end: "08:45" },
      { title: "Morning assembly", kind: "custom", start: "08:45", end: "09:00" },
      { title: "Break (tuck shop)", kind: "custom", start: "10:30", end: "11:00" },
      { title: "Lunch", kind: "meal", start: "12:30", end: "13:30" },
      { title: "Afternoon assembly", kind: "custom", start: "13:45", end: "14:00" },
      { title: "Supervised free time", kind: "custom", start: "16:00", end: "17:20" },
      { title: "Tuck shop", kind: "custom", start: "16:30", end: "17:00" },
      { title: "Dinner", kind: "meal", start: "17:30", end: "18:30" },
      { title: "Evening assembly", kind: "custom", start: "19:00", end: "19:15" },
      { title: "Phone collection", kind: "custom", start: "22:15", end: "22:30" },
      { title: "Lights out", kind: "night", start: "22:30", end: "23:00" },
    ],
  },
];

// ---- staff pool + key staff (from the weekly staff-scheduling feed) --------

function inStay(row, dateStr) {
  if (!row.arrivalDate || !row.departureDate) return true;
  return dateStr >= row.arrivalDate && dateStr <= row.departureDate;
}

function onDuty(row, dateStr) {
  return Array.isArray(row.scheduledDates) && row.scheduledDates.includes(dateStr);
}

function roleSummary(row) {
  const roles = Array.isArray(row.roles) ? row.roles : [];
  if (roles.length) return roles.map((r) => String(r)).join(" · ");
  const quals = Array.isArray(row.qualifications) ? row.qualifications : [];
  if (quals.length) return quals.join(" · ");
  return "Staff";
}

export function buildStaffPool(weekly, dateStr) {
  const rows = asArray(weekly && weekly.rows).length ? weekly.rows : asArray(weekly);
  return rows
    .filter((r) => r.staff && inStay(r, dateStr))
    .map((r) => {
      const off = !onDuty(r, dateStr);
      return {
        id: String(r.nsrId ?? r.adultId ?? r.name),
        staffId: r.staffId ?? r.adultId ?? null,
        name: r.name,
        role: off ? "day off" : roleSummary(r),
        off,
      };
    })
    .sort((a, b) => Number(a.off) - Number(b.off) || String(a.name).localeCompare(String(b.name)));
}

const firstName = (n) => String(n || "").trim().split(/\s+/)[0] || "";

export function buildKeyStaff(weekly, dateStr) {
  const rows = (asArray(weekly && weekly.rows).length ? weekly.rows : asArray(weekly))
    .filter((r) => r.staff && inStay(r, dateStr) && onDuty(r, dateStr));
  const has = (arr, re) => Array.isArray(arr) && arr.some((x) => re.test(String(x)));
  const pick = (pred) => rows.filter(pred).map((r) => firstName(r.name));
  return {
    campManager: pick((r) => has(r.roles, /camp.?manager/i)),
    dsl: pick((r) => r.dsl === true || has(r.qualifications, /^dsl$/i)),
    firstAid: pick((r) => r.firstaid === true || has(r.qualifications, /first.?aid/i)),
  };
}
